var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useMutation } from '@tanstack/react-query';
import Typography, { typographyVariants } from 'root/src/client/web/typography/Typography';
import { SponsorshipTerms } from 'root/src/client/v2/drop/components/SponsorshipTerms/SponsorshipTerms';
import { sponsorshipRegularTerms } from 'root/src/shared/descriptions/modules/howToEarnPointsModal';
import { useDropShortUrl } from 'root/src/client/v2/drop/hooks/useDropShortUrl';
import { TextField } from 'root/src/client/v2/common/components/form/TextField';
import { ButtonNew, ButtonNewVariant } from 'root/src/client/v2/common/components/form/ButtonNew';
import { useDareDropApi } from 'root/src/client/v2/common/hooks/useDareDropApi';
import { DEFAULT_MODAL_OPTIONS, ModalContextProvider, useModal } from 'root/src/client/v2/common/contexts/modal.context';
import { useDropEvent } from 'root/src/client/logic/dropEvent/hooks/useDropEvent';
import { useAppDispatch } from 'root/src/client/logic/helpers/useStore';
import apiRequest from 'root/src/client/logic/api/thunks/apiRequest';
import { ModalLayout } from 'root/src/client/v2/common/layouts/ModalLayout';
import { MAX_CREATE_POINTS_APPLICATION_LENGTH, } from 'root/src/server/modules/points/endpoints/createPointsApplication.endpoint';
import { dropEndpointIds } from 'root/src/shared/drops/drops.constants';
import { pointsEndpointIds, STREAM_DATE_IGNORED_CAMPAIGN_IDS } from 'root/src/shared/points/points.constants';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { FormProvider, useController, useForm, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import moment from 'moment';
import 'moment-timezone';
import { ErrorField } from 'root/src/client/v2/_pages/add-campaign/components/AddCampaignForm/ErrorField';
import toast from 'react-hot-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { memo } from 'react';
export const PointsApplicationModal = () => {
    const drop = useDropEvent();
    const modal = useModal();
    if (!drop) {
        return null;
    }
    return (<ModalLayout>
			{/* Additional provider needs for the inner `ReservedKeySelectionModal` modal */}
			<ModalContextProvider>
				<PointsApplicationModalContent drop={drop} mainModal={modal}/>
			</ModalContextProvider>
		</ModalLayout>);
};
const PointsApplicationModalContent = ({ drop, mainModal }) => {
    const { shortUrl, onSubmit, form, } = usePointsApplicationModal(drop, mainModal);
    const streamDate = form.watch('streamDate');
    const isTermsAccepted = form.watch('isTermsAccepted');
    const isStreamDateFieldVisible = !(drop.isLive || STREAM_DATE_IGNORED_CAMPAIGN_IDS.includes(drop.id));
    return (<>
			<FormProvider {...form}>
				<div className="flex flex-col justify-center items-center max-w-[700px] min-w-[350px]">
					<div className="flex flex-col gap-1 justify-center items-center ">
						<Typography variant={typographyVariants.heading2}>
							Apply for points
						</Typography>
					</div>
					<div className="border-[0.5px] w-[144px] border-light-4 mb-[30px] mt-[30px]"/>
					<div className="text-xl m-[25px]">
						We’ll approve your points application within 48 hours.
						You need to be approved to earn points.
						Let us know why you think you’re a good fit:
					</div>
					<CommentField />
					{isStreamDateFieldVisible && <StreamDateField drop={drop}/>}
					<div className="border-[0.5px] w-[144px] border-light-4 mb-[30px] mt-[30px]"/>
					<SponsorshipTerms terms={sponsorshipRegularTerms} dropEvent={drop} shortUrl={shortUrl} canEarnPoints isTermsAccepted={false} canUserReclaimReservation={false} streamDate={streamDate}/>
					<div className="flex flex-col justify-start py-2 pb-4">
						{!drop.isGameFree && (<ReserveInitialProductField />)}
						<TermsCheckboxField />
					</div>
					<ButtonNew disabled={!isTermsAccepted || form.formState.isSubmitting} className="w-[200px] mb-5" onClick={onSubmit}>
						Apply for points
					</ButtonNew>
				</div>
			</FormProvider>
		</>);
};
const usePointsApplicationFormContext = () => {
    return useFormContext();
};
const ReserveInitialProductField = () => {
    const form = usePointsApplicationFormContext();
    const { field } = useController({
        control: form.control,
        name: 'reserveInitialProduct',
    });
    return (<FormControlLabel control={(<Checkbox checked={field.value} onChange={(_, isChecked) => {
                field.onChange(isChecked);
            }}/>)} label={(<span className="text-sm select-none">
					I would need the game key.
				</span>)}/>);
};
const PointsApplicationBaseFormSchema = z.object({
    comment: z.string().max(MAX_CREATE_POINTS_APPLICATION_LENGTH),
    isTermsAccepted: z.boolean({
        required_error: 'You must accept the terms to apply for points',
    }),
    reserveInitialProduct: z.boolean().optional(),
});
const PointsApplicationComingSoonFormSchema = PointsApplicationBaseFormSchema.extend({
    streamDate: z.string({
        required_error: 'Stream date is required',
    }).datetime(),
});
const usePointsApplicationModal = (drop, mainModal) => {
    const schema = moment.utc(drop.liveDate).isAfter(moment())
        ? PointsApplicationComingSoonFormSchema
        : PointsApplicationBaseFormSchema;
    const form = useForm({
        defaultValues: {
            comment: '',
            isTermsAccepted: false,
            reserveInitialProduct: false,
        },
        mode: 'all',
        reValidateMode: 'onChange',
        resolver: zodResolver(schema),
    });
    const { fetcher } = useDareDropApi();
    const shortUrlQuery = useDropShortUrl({ dropId: drop.id, enabled: false });
    const dispatch = useAppDispatch();
    const modal = useModal();
    const applyForPointsMutation = useMutation({
        mutationKey: [pointsEndpointIds.CREATE_POINTS_APPLICATION],
        mutationFn: (values) => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            const res = yield fetcher({
                endpointId: pointsEndpointIds.CREATE_POINTS_APPLICATION,
                payload: {
                    dropId: drop.id,
                    comment: (_a = values.comment) !== null && _a !== void 0 ? _a : undefined,
                    streamDate: values.streamDate,
                    reserveInitialProduct: values.reserveInitialProduct,
                },
            });
            yield dispatch(apiRequest(dropEndpointIds.DROP_GET_EVENT, { recordId: drop.mnemonicId }));
            return res;
        }),
    });
    const onSubmit = form.handleSubmit((values) => __awaiter(void 0, void 0, void 0, function* () {
        if (!values.reserveInitialProduct && !drop.isGameFree) {
            const shouldContinue = yield new Promise((resolve) => {
                modal.show(<NoReservedInitialProductConfirmationModal onSubmit={(result) => {
                        modal.hide();
                        resolve(result);
                    }}/>, { isCloseable: false, zIndex: DEFAULT_MODAL_OPTIONS.zIndex + 1 });
            });
            if (!shouldContinue) {
                console.warn('User cancelled points application without reserving initial product');
                return;
            }
        }
        try {
            yield applyForPointsMutation.mutateAsync(values);
            mainModal.hide();
        }
        catch (err) {
            console.info(values);
            console.error(err);
            const message = err instanceof Error ? err.message : 'Failed to apply for points';
            toast.error(message);
        }
    }));
    return {
        shortUrl: shortUrlQuery.data,
        onSubmit,
        form,
    };
};
const CommentField = () => {
    const form = usePointsApplicationFormContext();
    const { field, fieldState } = useController({
        control: form.control,
        name: 'comment',
    });
    return (<>
			<TextField label={'Why do you think you\'re a good fit for this drop?'} multiline rows={3} className="w-[350px]" invalid={fieldState.invalid} value={field.value} onChange={(e) => {
            field.onChange(e.target.value);
        }} placeholder="Type here"/>
			<div className="flex w-[350px]">
				<span className={`m-1 ml-auto font-medium ${fieldState.invalid
            ? 'text-[#d32f2f]'
            : ''}`}>
					{field.value.length} / {MAX_CREATE_POINTS_APPLICATION_LENGTH}
				</span>
			</div>
		</>);
};
const isDateAvailable = (unit, date, drop) => {
    return moment(date).isBetween(moment.utc(drop.liveDate), moment.utc(drop.liveDate).add(drop.pointsReservationHours, 'hour'), unit, '[]');
};
export const StreamDateField = ({ drop }) => {
    var _a;
    const form = usePointsApplicationFormContext();
    const { field, fieldState } = useController({
        control: form.control,
        name: 'streamDate',
    });
    return (<div className="w-[350px]">
			<LocalizationProvider dateAdapter={AdapterMoment}>
				<DateTimePicker disablePast shouldDisableDate={(date) => {
            return !isDateAvailable('day', moment(date), drop);
        }} shouldDisableMonth={(date) => {
            return !isDateAvailable('month', moment(date), drop);
        }} shouldDisableYear={(date) => {
            return !isDateAvailable('year', moment(date), drop);
        }} shouldDisableTime={(time) => {
            return !isDateAvailable('hour', moment(time), drop);
        }} value={field.value ? moment(field.value) : null} onChange={(date) => {
            const formattedDate = moment(date)
                .utc()
                .format();
            field.onChange(formattedDate);
        }} label="Planning to stream at" sx={{ width: '350px' }}/>
				<ErrorField error={(_a = fieldState.error) === null || _a === void 0 ? void 0 : _a.message}/>
				<p className="text-sm mt-2">
					Please add date and time when you are planning to stream in case <span className="font-bold">{drop.brand.brandName}</span> wants to pop in and say hi or promote you on their socials.
				</p>
			</LocalizationProvider>
		</div>);
};
const TermsCheckboxField = () => {
    const form = usePointsApplicationFormContext();
    const { field } = useController({
        control: form.control,
        name: 'isTermsAccepted',
    });
    return (<FormControlLabel control={(<Checkbox checked={field.value} onChange={(_, isChecked) => {
                field.onChange(isChecked);
            }}/>)} label={(<span className="text-sm select-none">
					I agree to the terms for earning Points on this Drop. <span className="text-pink font-bold text-xl" aria-label="required">*</span>
				</span>)}/>);
};
const NoReservedInitialProductConfirmationModal = memo(({ onSubmit }) => {
    return (<ModalLayout>
			<div className="flex flex-col justify-center items-center max-w-[700px] min-w-[350px] p-5">
				<p className="text-lg font-bold">
					Are you sure?
				</p>
				<div className="border-[0.5px] w-[144px] border-light-4 mb-[30px] mt-[30px]"/>
				<div className="py-4 flex flex-col gap-2">
					<p>
						Do you want to proceed without reserving the game key? If you choose not to reserve it,
						there is a risk that the keys may run out before you can claim one.
					</p>
					<p className="font-bold">Ignore it if you already have a key, or won't be needing one.</p>
				</div>
				<div className="flex gap-4 mt-6">
					<ButtonNew variant={ButtonNewVariant.Error} onClick={() => {
            onSubmit(false);
        }}>
						No, go back
					</ButtonNew>
					<ButtonNew onClick={() => {
            onSubmit(true);
        }}>
						Yes
					</ButtonNew>
				</div>
			</div>
		</ModalLayout>);
});
